import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Crumbs, Thumb } from "../components/Bits";
import { Head } from "../components/Head";
import { ShareLink } from "../components/Share";
import { useData } from "../lib/data";
import { API, addToPlan, amount, origin, store } from "../lib/util";

const CHIPS = ["kylling", "hakket oksekød", "pasta", "ris", "kartofler", "løg", "gulerødder", "æg", "dåsetomater", "bønner", "ost", "fløde", "bacon", "laks"];
const EMPTY = "Skriv mindst én ingrediens";
const AI_DOWN = "AI-forslag er ikke tilgængelige lige nu";
const split = v => v.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
const post = (path, body) => fetch(`${API}/fridge/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export function MatchCard({ m, r }) {
  const href = `/opskrifter/${r.slug}/`;
  return (
    <article className="fr-card" data-testid={`fridge-match-${r.slug}`}>
      <a href={href} aria-label={r.title}><Thumb key={r.slug} r={r} lazy><div className="sticker"><b>{r.price}</b><small>kr.</small></div></Thumb></a>
      <div className="fr-b">
        <h3><a href={href}>{r.title}</a></h3>
        <div className="fr-meta"><span>{`${r.time} min`}</span><span>{`ca. ${r.price} kr. pr. person`}</span></div>
        <p className="fr-have" data-testid={`fridge-have-${r.slug}`}><b>Du har:</b> {m.have.join(", ")}</p>
        <p className="fr-miss" data-testid={`fridge-missing-${r.slug}`}><b>Mangler:</b> {m.missing.length ? m.missing.join(", ") : "ingenting, du har det hele"}</p>
        <div className="fr-acts">
          <button className="btn" type="button" onClick={() => addToPlan(r.slug, Math.min(12, Math.max(1, +store.get("people", 4) || 4)))} data-testid={`fridge-add-plan-${r.slug}`}>Tilføj til madplan</button>
          <a className="btn ghost" href={href}>Se opskrift</a>
        </div>
      </div>
    </article>
  );
}

export function AiCard({ x, i }) {
  return (
    <article className="fr-ai" data-testid={`fridge-ai-recipe-${i}`}>
      <span className="fr-label">AI-forslag</span>
      <h3>{x.title}</h3><p>{x.description}</p>
      <div className="pills"><span className="pill">{`ca. ${x.price_per_person} kr. pr. person`}</span><span className="pill">{`${x.time} min`}</span><span className="pill">{`${x.servings} portioner`}</span></div>
      <div className="fr-cols">
        <div><h4>Ingredienser</h4><ul className="ing">{x.ingredients.map((g, k) => (
          <li key={k}><span className="amt">{g.amount ? amount(g.amount, g.unit) : ""}</span><span>{g.name}</span></li>))}</ul></div>
        <div><h4>Sådan gør du</h4><ol className="steps">{x.steps.map((s, k) => <li key={k}>{s}</li>)}</ol></div>
      </div>
      <p className="fr-note">Forslaget er lavet af AI og ikke testet i vores køkken. Smag til, og sørg for, at kød og fisk er gennemstegt.</p>
    </article>
  );
}

export default function Fridge() {
  const { BY } = useData();
  const loc = useLocation();
  const nav = useNavigate();
  const started = useRef(false);
  const [val, setVal] = useState(() => store.get("fridge", ""));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [ai, setAi] = useState(null);

  const run = async text => {
    if (!text.replace(/[,;\s]/g, "")) { setErr(EMPTY); return; }
    setErr(""); setBusy(true); setRes(null); setAi(null);
    try {
      const r = await post("search", { ingredients: text });
      const d = await r.json();
      if (!r.ok) { setErr(typeof d.detail === "string" ? d.detail : "Noget gik galt. Prøv igen."); return; }
      setRes(d);
      if (!d.needs_ai) return;
      setAiBusy(true);
      try {
        const a = await post("ai", { search_id: d.search_id });
        setAi(a.ok ? await a.json() : { status: "unavailable", message: AI_DOWN });
      } catch (e) { setAi({ status: "unavailable", message: AI_DOWN }); }
      finally { setAiBusy(false); }
    } catch (e) { setErr("Noget gik galt. Prøv igen."); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const i = new URLSearchParams(loc.search).get("i");
    if (i) { setVal(i); store.set("fridge", i); nav("/koeleskabet/", { replace: true }); run(i); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const change = v => { setVal(v); store.set("fridge", v); setErr(""); };
  const items = split(val).map(s => s.toLowerCase());
  const toggle = c => change(items.includes(c) ? split(val).filter(s => s.toLowerCase() !== c).join(", ") : [...split(val), c].join(", "));
  const nonFood = ai?.status === "non_food";
  const loading = busy || aiBusy;
  const matches = res ? res.matches.filter(m => BY[m.recipe.slug]) : [];

  return (
    <>
      <Head title="Køleskabet: find aftensmad ud fra det, du har" path="koeleskabet/"
        desc="Skriv hvad du har i køleskabet, og få forslag til billig aftensmad: opskrifter fra Hverdagsmaden og nye AI-forslag." />
      <Crumbs trail={[[null, "Køleskabet"]]} />
      <div className="wrap" data-testid="fridge-page">
        <div className="ptop"><h1>Hvad har du i køleskabet?</h1>
          <p className="lead">Skriv de råvarer, du har, så finder vi aftensmad på siden, der passer. Passer ingen godt, laver vores AI et par billige forslag.</p></div>
        <form className="tool fr-form" onSubmit={e => { e.preventDefault(); run(val); }} data-testid="fridge-form">
          <div className="fr-row">
            <div className="fld"><label htmlFor="fr-in">Dine ingredienser (adskil med komma)</label>
              <input id="fr-in" value={val} placeholder="fx hakket oksekød, pasta, dåsetomater, løg" autoComplete="off" onChange={e => change(e.target.value)} data-testid="fridge-input" /></div>
            <button className="btn" type="submit" disabled={loading} aria-busy={loading ? "true" : "false"} data-testid="fridge-submit">{busy ? <><span className="fr-spin w" />Finder aftensmad…</> : "Find aftensmad"}</button>
          </div>
          <div className="fr-chips" role="group" aria-label="Tilføj ingrediens">{CHIPS.map(c => (
            <button key={c} type="button" className="fchip" aria-pressed={String(items.includes(c))} onClick={() => toggle(c)} data-testid={`fridge-chip-${c.replace(/\s/g, "-")}`}>{c}</button>))}</div>
          <p className="fr-hint">Salt, peber, olie, mel, sukker og krydderier regner vi med, at du har.{res && <> <span data-testid="fridge-remaining">{`Du har ${res.remaining} af ${res.limit} søgninger tilbage i dag.`}</span></>}</p>
          {(err || nonFood) && <p className="fr-msg" role="alert" data-testid="fridge-error">{err || ai.message}</p>}
        </form>

        {res && !nonFood && (
          <div className="fr-share" data-testid="fridge-share-bar">
            <p>Vis familien, hvad I kan lave i aften.</p>
            <ShareLink url={`${origin()}/api/del/koeleskabet/${res.share_id}`} title="Aftensmad ud fra mit køleskab" label="Del forslag" testid="fridge-share-button" />
          </div>
        )}
        {res && !nonFood && (
          <section className="fr-sec" data-testid="fridge-matches">
            <div className="sh"><h2>Opskrifter du kan lave</h2></div>
            {matches.length ? <div className="fr-grid">{matches.map(m => <MatchCard key={m.recipe.slug} m={m} r={BY[m.recipe.slug]} />)}</div>
              : <p className="fr-empty" data-testid="fridge-no-matches">Ingen opskrifter på siden passer godt til det, du har.</p>}
          </section>
        )}
        {(aiBusy || (ai && !nonFood)) && (
          <section className="fr-sec" data-testid="fridge-ai">
            <div className="sh"><h2>AI-forslag</h2></div>
            {aiBusy && <div className="fr-loading" data-testid="fridge-ai-loading"><span className="fr-spin" />Laver AI-forslag ud fra dine råvarer… Det tager typisk 10-20 sekunder.</div>}
            {ai?.status === "ok" && <div className="fr-ailist">{ai.recipes.map((x, i) => <AiCard key={i} x={x} i={i} />)}</div>}
            {ai?.status === "unavailable" && <p className="note" data-testid="fridge-ai-unavailable">{ai.message}</p>}
          </section>
        )}
      </div>
    </>
  );
}
